import { computed, reaction, makeObservable } from "mobx";
import { computedFn } from "mobx-utils";
import { disposer as handler } from "@draftkings/sportsbook-common-utils";
import { DataCondition } from "../../../contracts";
import { getSelectedFilterName, getNoBetsErrorMessage } from "../../Shared";
import { getLiveScoreboardComponent, getNavLinkComponent, isPrePackUnlocked, unlockPrePack, lockPrePack } from "../helpers";
export class PrePacksState {
    constructor(options) {
        this.setParamsMap = {
            Event: params => {
                var _a;
                return this.parser.setParams({
                    eventId: (_a = params.mode) === null || _a === void 0 ? void 0 : _a.id,
                    restorationOptions: params.restorationOptions,
                });
            },
            League: params => {
                var _a;
                return this.parser.setParams({
                    leagueId: (_a = params.mode) === null || _a === void 0 ? void 0 : _a.id,
                    restorationOptions: params.restorationOptions,
                });
            },
        };
        this.getEventScoreboardParsedData = computedFn((eventId) => this.parser.getEventScoreboardParsedData(eventId));
        this.getChunkedPrePacks = computedFn((eventId) => this.chunkedPrePacksCalcMap[this.modeType](eventId));
        this.chunkedPrePacksCalcMap = {
            Event: (eventId) => {
                const prePacks = this.getPrePacks(eventId);
                const chunkSize = this.config.lazyRendering.lazyRenderChunkSize;
                let index = 0;
                let chunkedPrePacksIndex = 0;
                const chunkedPrePacks = new Array(Math.ceil(prePacks.length / chunkSize));
                for (let i = 0; i < prePacks.length; i += chunkSize) {
                    chunkedPrePacks[chunkedPrePacksIndex++] = {
                        id: `chunk_${chunkedPrePacksIndex}`,
                        prePacks: prePacks.slice(index, (index += chunkSize)),
                        lazyRendering: this.config.lazyRendering,
                    };
                }
                return chunkedPrePacks;
            },
            League: () => ({
                getPrePacks: this.getPrePacks,
                lazyRendering: Object.assign(Object.assign({}, this.config.lazyRendering), { lazyRenderChunkSize: this.parser.filteredEvents.length }),
            })
        };
        this.getPrePacks = (eventId) => this.parser.filteredPrePacksByEventId
            .getData(eventId)
            .map(x => (Object.assign(Object.assign({}, x), { shouldTriggerUnlockAnimation: isPrePackUnlocked(x, this.unlockedPrePacksMap), onUnlockAnimationTriggered: unlockPrePack(x, this.unlockedPrePacksMap), onPrePackLocked: lockPrePack(x, this.unlockedPrePacksMap), toggleModifier: this.parser.getPrePackModifier(eventId, x.id), onClick: (prePackId, modifier) => {
                this.sendOutcomeActionAnalytics(eventId, x, modifier);
                this.onTogglePrePack(eventId, prePackId);
            } })));
        this.onSelectFilter = (options) => {
            this.sendSubcategoryTabAnalytics(options);
            this.parser.selectFilter(options.id);
        };
        this.onTogglePrePack = (eventId, prePackId) => {
            this.consumerCommands.freezeWager({
                eventId,
            });
            this.consumerCommands.syncSelections(this.parser.getSelectionsState(eventId, prePackId));
            this.parser.togglePrePack(eventId, prePackId);
        };
        this.onEmptyOrLoaded = () => {
            if (!this.isPrePacksLoadedFired) {
                this.consumerCommands.prePacksLoaded({
                    success: { hasData: this.isLoaded && this.events.length > 0 }
                });
                this.isPrePacksLoadedFired = true;
            }
        };
        this.onError = () => {
            if (!this.isPrePacksLoadedFired) {
                this.consumerCommands.prePacksLoaded({
                    error: this.parser.prePacksStatusError
                });
                this.isPrePacksLoadedFired = true;
            }
            else if (this.parser.prePacksStatusError) {
                this.consumerCommands.showError(this.parser.prePacksStatusError);
            }
        };
        this.mode = options.mode;
        this.dict = options.dict;
        this.config = options.config;
        this.inGameState = options.inGameState;
        this.parser = options.parser;
        this.condition = options.condition;
        this.consumerCommands = options.consumerCommands;
        this.disposers = [];
        this.isPrePacksLoadedFired = false;
        this.getNavLink = getNavLinkComponent(options.getNavLink);
        this.getEventPageNavLink = options.getEventPageNavLink;
        this.liveScoreboardComponent = getLiveScoreboardComponent(options.getLiveScoreboard);
        this.unlockedPrePacksMap = new Map();
        this.getDateCell = options.getDateCell;
        this.getTeamLogo = options.getTeamLogo;
        this.locale = options.locale;
        this.handleConditionMap = {
            [DataCondition.EMPTY]: this.onEmptyOrLoaded,
            [DataCondition.LOADED]: this.onEmptyOrLoaded,
            [DataCondition.LOADING]: handler,
            [DataCondition.ERROR]: this.onError,
        };
        makeObservable(this, {
            isEmpty: computed,
            isLoaded: computed,
            isLoading: computed,
            events: computed,
            filters: computed,
            selectedFilterId: computed,
            eventNameIdentifier: computed,
        });
    }
    get modeType() {
        return this.mode.type;
    }
    get noBetsProps() {
        return {
            title: this.dict.NoBetsTitle,
            info: this.condition.value === DataCondition.EMPTY ? getNoBetsErrorMessage(this.inGameState, this.dict.LiveNoBetsInfo, this.dict.NoBetsInfo) : getNoBetsErrorMessage(this.inGameState, this.dict.LiveNoBetsForFilterInfo, this.dict.NoBetsForFilterInfo),
        };
    }
    get viewGameLinesProps() {
        const showViewGameLinesButton = this.condition.value === DataCondition.EMPTY
            && this.mode.type === "Event"
            && this.mode.params.shouldNavigateOnEmptyScreen
            && this.inGameState !== "live";
        return {
            isVisible: showViewGameLinesButton,
            title: this.dict.NoBetsViewGameLines,
        };
    }
    get showOverlay() {
        return this.parser.isPrePackProcessing;
    }
    get scoreboardData() {
        return this.parser.scoreboardData;
    }
    get isEmpty() { return this.condition.value === DataCondition.EMPTY || (this.condition.value === DataCondition.LOADED && !this.events.length); }
    get isLoaded() { return this.filters.length > 0; }
    get isLoading() { return this.condition.value === DataCondition.LOADING; }
    get events() {
        return this.parser.filteredEvents;
    }
    get filters() {
        return this.parser.prePacksFilters.map((x, index) => (Object.assign(Object.assign({}, x), { isSelected: x.id === this.parser.selectedOrDefaultFilterId, onClick: this.onSelectFilter, index })));
    }
    get selectedFilterId() {
        return this.parser.selectedFilterId;
    }
    get eventNameIdentifier() {
        return this.parser.filteredEvents.reduce((_, e) => e.eventNameIdentifier, "");
    }
    activate() {
        this.disposers.push(reaction(() => this.condition.value, x => this.handleConditionMap[x]()));
        this.parser.activate();
    }
    deactivate() {
        this.parser.deactivate();
        this.disposers.forEach(x => x());
        this.disposers = [];
        this.isPrePacksLoadedFired = false;
        this.unlockedPrePacksMap.clear();
    }
    setParams(params) {
        if (params.mode) {
            this.mode.id = params.mode.id;
            this.mode.params = params.mode.params;
        }
        this.setParamsMap[this.modeType](params);
    }
    processSelections(data) {
        this.parser.processSelections(data);
    }
    setOddsStyle(oddsStyle) {
        this.parser.setOddsStyle(oddsStyle);
    }
    sendSubcategoryTabAnalytics(options) {
        this.consumerCommands.sendAnalytics({
            type: "SubcategoryTab",
            metadata: {
                text: options.name,
                rank: options.index + 1,
            },
        });
    }
    sendOutcomeActionAnalytics(eventId, prePack, modifier) {
        const filterName = getSelectedFilterName(this.filters, f => f.isSelected);
        prePack.selections.forEach(selection => {
            this.parser.sendOutcomeActionAnalytics(eventId, {
                prePackId: prePack.id,
                filterName,
                selectionId: selection.id,
                status: modifier === "default" ? "IsAdding" : "IsRemoving",
                isLive: prePack.isLive,
            });
        });
    }
}
//# sourceMappingURL=PrePacksState.js.map